/**
 * Service Worker `fetch` handler for the Web Share Target endpoint.
 *
 * Web equivalent of the iOS Share Extension's `viewDidLoad` → `validate` →
 * `SharePendingDraftStore.enqueue` flow, compressed into a single SW
 * function because the web platform delivers the share via an HTTP-style
 * POST the SW intercepts.
 *
 * THREAT MODEL — see `ShareTargetPayload.ts` and `ShareTargetPolicy.ts`
 * for the input-validation invariants. This file is responsible for the
 * I/O glue between (a) the browser's multipart `FormData`, (b) the pure
 * validation policy, and (c) the IDB draft store + composer redirect.
 *
 * INVARIANTS
 *   - The handler NEVER calls `fetch()` outbound. The shared payload is
 *     read entirely from the request body the OS handed us.
 *   - The handler NEVER auto-sends. The successful path is:
 *
 *       persist draft → 303 redirect to /compose?draft=<id>
 *
 *     The composer is responsible for showing the user the parsed items
 *     and waiting for an explicit confirm before invoking the encrypt +
 *     send pipeline.
 *   - On validation failure the handler redirects to a stable
 *     `/compose?error=<kind>` URL so the composer can surface a readable
 *     message; it does NOT leak the offending bytes or the precise
 *     numeric value back to the host page via the URL.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. It uses
 *   `Request` / `Response` / `FormData` / `Blob` which are SW-compatible.
 *
 * INSTALLATION (the consumer's responsibility, see docs):
 *
 *     // In sw.ts:
 *     import { handleShareTargetFetch, SHARE_TARGET_PATH } from
 *       "e2ee/share-target/ShareTargetFetchHandler";
 *
 *     self.addEventListener("fetch", (event) => {
 *       const url = new URL(event.request.url);
 *       if (event.request.method === "POST" && url.pathname === SHARE_TARGET_PATH) {
 *         event.respondWith(handleShareTargetFetch(event.request));
 *       }
 *     });
 */

import { validateSharedPayload } from "./ShareTargetPolicy";
import type { ValidationDecision } from "./ShareTargetPolicy";
import {
  DEFAULT_DRAFT_TTL_MS,
  ShareTargetStore,
  newDraftId,
} from "./ShareTargetStore";
import type {
  ShareItem,
  ShareItemKind,
  SharePendingDraft,
  SharePayloadError,
} from "./ShareTargetPayload";

/** The path the PWA manifest's `share_target.action` points to. */
export const SHARE_TARGET_PATH = "/share-target";

/** The composer path the SW redirects to on validation success / failure. */
export const COMPOSER_PATH = "/compose";

// ---------------------------------------------------------------------------
// Outcome — closed discriminated union for testability
// ---------------------------------------------------------------------------

export type ShareTargetOutcome =
  | { readonly kind: "redirected"; readonly draftId: string; readonly redirectUrl: string }
  | { readonly kind: "rejected"; readonly error: SharePayloadError; readonly redirectUrl: string };

/**
 * Pure function: given a parsed `FormData` and the current clock, run the
 * full validate-and-persist pipeline and return the outcome. Separated
 * from the `Request`/`Response` adapter so unit tests can drive the
 * pipeline with a hand-built `FormData` and inspect the result without
 * spinning up an SW.
 *
 * The persisted draft (on success) is written to `store`. On rejection,
 * nothing is written.
 */
export async function processShareTargetFormData(
  form: FormData,
  store: ShareTargetStore,
  now: number = Date.now(),
  materialisedFileBytes?: readonly Uint8Array[],
): Promise<ShareTargetOutcome> {
  const { items, caption } = parseFormData(form, materialisedFileBytes);

  const decision: ValidationDecision = validateSharedPayload(items, caption);
  if (decision.kind === "reject") {
    return {
      kind: "rejected",
      error: decision.error,
      redirectUrl: composerRejectUrl(decision.error),
    };
  }

  const id = newDraftId(now);
  const draft: SharePendingDraft = {
    id,
    items,
    captionText: caption,
    createdAt: now,
    expiresAt: now + DEFAULT_DRAFT_TTL_MS,
  };
  await store.put(draft);

  return {
    kind: "redirected",
    draftId: id,
    redirectUrl: composerDraftUrl(id),
  };
}

// ---------------------------------------------------------------------------
// FormData → ShareItem[] parser
// ---------------------------------------------------------------------------

/**
 * Map a `FormData` from a `share_target` POST into `(items, caption)`.
 *
 * The PWA manifest declares the field names; we use the standard names
 * specified by the Web Share Target spec:
 *
 *   - "title"  → folded into `caption` (some OSes hand the page title here)
 *   - "text"   → folded into `caption` if no other text; OR a `text` item
 *   - "url"    → a `url` item
 *   - "files"  → one or more `image` / `video` / `file` items
 *
 * The exact merge rule mirrors what Android's Web Share Target does in
 * Chrome: when both `title` and `text` are present, `caption` is
 * `"<title>\n<text>"`; when only one is present, that one is the caption.
 * If `text` looks like a single URL and no `url` field exists, it becomes
 * a `url` item. Otherwise `text` becomes a `text` item.
 *
 * This is intentionally NOT clever — every coercion is documented above
 * so the validation policy gets a predictable shape regardless of OS.
 */
export function parseFormData(
  form: FormData,
  materialisedFileBytes?: readonly Uint8Array[],
): {
  items: readonly ShareItem[];
  caption: string | undefined;
} {
  const title = formStringOrUndefined(form.get("title"));
  const text = formStringOrUndefined(form.get("text"));
  const url = formStringOrUndefined(form.get("url"));
  const files = form.getAll("files");

  const items: ShareItem[] = [];

  // URL field → url item
  if (url !== undefined) {
    items.push({ kind: "url", plaintextString: url });
  }

  // Files → image/video/file items based on top-level MIME category.
  // Bytes come from the caller-supplied `materialisedFileBytes` array
  // (positional, indexed in the same order as `form.getAll("files")`).
  // This handler NEVER refetches bytes from the network — the OS handed
  // them to us in the FormData and the async wrapper materialised them
  // via `Blob.arrayBuffer()` before this synchronous parse step.
  let fileIdx = 0;
  for (const file of files) {
    if (!(file instanceof Blob)) {
      fileIdx += 1;
      continue; // form.getAll can return strings
    }
    const blob = file as Blob & { name?: string };
    const mime = blob.type || "application/octet-stream";
    const bytes = materialisedFileBytes?.[fileIdx] ?? new Uint8Array(0);
    const kind: ShareItemKind = mimeKindCategory(mime);
    items.push({
      kind,
      mimeType: mime,
      plaintextBytes: bytes,
      suggestedFilename: blob.name,
    });
    fileIdx += 1;
  }

  // Caption: merge title + text. If text is a sole URL and no `url` field
  // was given, route it through `url` instead of caption.
  let captionPieces: string[] = [];
  if (title !== undefined && title.length > 0) captionPieces.push(title);
  if (text !== undefined && text.length > 0) {
    if (url === undefined && looksLikeUrl(text)) {
      items.push({ kind: "url", plaintextString: text });
    } else {
      captionPieces.push(text);
    }
  }
  const caption = captionPieces.length > 0 ? captionPieces.join("\n") : undefined;

  return { items, caption };
}

/**
 * Async wrapper that materialises every Blob in the FormData to bytes
 * BEFORE handing off to `processShareTargetFormData`. The SW handler must
 * call this first because `processShareTargetFormData` is synchronous over
 * the parsed items.
 */
export async function materialiseAndProcess(
  form: FormData,
  store: ShareTargetStore,
  now: number = Date.now(),
): Promise<ShareTargetOutcome> {
  // Pre-materialise every "files" entry's bytes by reading them DIRECTLY
  // from the FormData (no outbound `fetch`). `parseFormData` is sync and
  // can't itself await `Blob.arrayBuffer()`, so we materialise here and
  // feed the resulting bytes back through a side Map keyed by entry
  // index (FormData re-wraps Blobs on `append`, so reference identity is
  // not reliable across `getAll` calls; positional order IS preserved).
  const files = form.getAll("files");
  const materialised: Uint8Array[] = [];
  for (const file of files) {
    if (!(file instanceof Blob)) {
      materialised.push(new Uint8Array(0));
      continue;
    }
    materialised.push(new Uint8Array(await file.arrayBuffer()));
  }
  return processShareTargetFormData(form, store, now, materialised);
}

// ---------------------------------------------------------------------------
// Request / Response adapter
// ---------------------------------------------------------------------------

/**
 * Standard SW `event.respondWith(...)` handler for the share-target POST.
 * Parses the request, runs the pipeline, returns a 303 redirect Response.
 *
 * On any unexpected error (malformed multipart, IDB failure) we redirect
 * to `/compose?error=internal` — we never leak the precise reason to the
 * host page via a JSON response body.
 */
export async function handleShareTargetFetch(req: Request): Promise<Response> {
  try {
    const form = await req.formData();
    const outcome = await materialiseAndProcess(form, ShareTargetStore.shared);
    return Response.redirect(outcome.redirectUrl, 303);
  } catch {
    return Response.redirect(composerInternalErrorUrl(), 303);
  }
}

// ---------------------------------------------------------------------------
// URL builders
// ---------------------------------------------------------------------------

function composerDraftUrl(draftId: string): string {
  // The composer reads `?draft=<id>` and calls
  // `ShareTargetStore.shared.get(id)` to materialise.
  return `${COMPOSER_PATH}?draft=${encodeURIComponent(draftId)}`;
}

function composerRejectUrl(error: SharePayloadError): string {
  // Only the error.kind goes into the URL; counts / byte sizes / MIME
  // strings stay out so we don't echo attacker-controlled bytes back into
  // the page.
  return `${COMPOSER_PATH}?error=${encodeURIComponent(error.kind)}`;
}

function composerInternalErrorUrl(): string {
  return `${COMPOSER_PATH}?error=internal`;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function formStringOrUndefined(v: FormDataEntryValue | null): string | undefined {
  if (v === null) return undefined;
  if (typeof v === "string") return v;
  return undefined; // Blob in a non-files field — ignore.
}

/**
 * Coarse mapping from a top-level MIME category to a `ShareItemKind`. The
 * detailed MIME allow-list happens later in `ShareTargetPolicy.validate`.
 */
function mimeKindCategory(mime: string): ShareItemKind {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "file";
}

/**
 * Cheap heuristic: a single token starting with `http://` or `https://`
 * looks like a URL. We do not parse — `validateSharedPayload` will catch
 * structural issues elsewhere and the composer re-validates URLs anyway.
 */
function looksLikeUrl(s: string): boolean {
  const trimmed = s.trim();
  if (/\s/.test(trimmed)) return false;
  return trimmed.startsWith("https://") || trimmed.startsWith("http://");
}
